import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Spinner, Stack, Text} from '@chakra-ui/react'
import {Component} from '@salesforce/commerce-sdk-react/components'
import {FormattedMessage, useIntl} from 'react-intl'
import usePdpDeliveryEstimations from '@app/hooks/use-pdp-delivery-estimations'
import {formatDate} from '@app/utils/basket-utils'

/**
 * PDCShippingPDPDetailsLayout component.
 *
 * This component is responsible for rendering the shipping details layout
 * on the Product Detail Page (PDP) based on the provided regions.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.regions - An array of region objects that determine
 * the layout and components to be rendered.
 *
 * @returns {JSX.Element} The rendered shipping details layout.
 */
export const PDCShippingPDPDetailsLayout = ({regions}) => {
    const intl = useIntl()
    const {data, loading, fetchEstimations} = usePdpDeliveryEstimations()
    const estimations = data?.estimations || {}
    const wrapperRef = useRef(null)
    const [hasFetched, setHasFetched] = useState(false)
    let components = []
    if (regions) {
        components = regions[0].components || []
    }

    useEffect(() => {
        if (!wrapperRef.current || hasFetched) {
            return undefined
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setHasFetched(true)
                fetchEstimations()
                observer.disconnect()
            }
        })

        observer.observe(wrapperRef.current)

        return () => observer.disconnect()
    }, [fetchEstimations, hasFetched])

    const formatMethodDate = (method) =>
        method?.c_calculatedShippingDateTo
            ? formatDate(method.c_calculatedShippingDateTo, intl.locale)
            : null

    const standardDate = formatMethodDate(estimations.standard || estimations.international)
    const expressDate = formatMethodDate(estimations.express)
    const secondDayAirDate = formatMethodDate(estimations.secondDayAir)
    const nextDayAirDate = formatMethodDate(estimations.nextDayAir)
    const estimationRows = []

    if (standardDate) {
        estimationRows.push({
            key: 'standard',
            title: intl.formatMessage({
                id: 'product_detail.shipping_estimation.standard.title',
                defaultMessage: 'STANDARD SHIPPING'
            }),
            message: intl.formatMessage(
                {
                    id: 'product_detail.shipping_estimation.standard',
                    defaultMessage: 'Estimated delivery from {date}.'
                },
                {
                    date: standardDate
                }
            )
        })
    }

    if (expressDate) {
        estimationRows.push({
            key: 'express',
            title: intl.formatMessage({
                id: 'product_detail.shipping_estimation.express.title',
                defaultMessage: 'EXPRESS SHIPPING'
            }),
            message: intl.formatMessage(
                {
                    id: 'product_detail.shipping_estimation.express',
                    defaultMessage: 'Estimated delivery from {date}.'
                },
                {
                    date: expressDate
                }
            )
        })
    }

    if (secondDayAirDate) {
        estimationRows.push({
            key: 'secondDayAir',
            title: intl.formatMessage({
                id: 'product_detail.shipping_estimation.express.title',
                defaultMessage: 'EXPRESS SHIPPING'
            }),
            label: intl.formatMessage({
                id: 'product_detail.shipping_estimation.second_day_air.label',
                defaultMessage: 'Second day air:'
            }),
            message: intl.formatMessage(
                {
                    id: 'product_detail.shipping_estimation.second_day_air',
                    defaultMessage: 'Estimated delivery from {date}.'
                },
                {
                    date: secondDayAirDate
                }
            )
        })
    }

    if (nextDayAirDate) {
        estimationRows.push({
            key: 'nextDayAir',
            title: secondDayAirDate
                ? null
                : intl.formatMessage({
                      id: 'product_detail.shipping_estimation.express.title',
                      defaultMessage: 'EXPRESS SHIPPING'
                  }),
            label: intl.formatMessage({
                id: 'product_detail.shipping_estimation.next_day_air.label',
                defaultMessage: 'Next Day air:'
            }),
            message: intl.formatMessage(
                {
                    id: 'product_detail.shipping_estimation.next_day_air',
                    defaultMessage: 'Estimated delivery from {date}.'
                },
                {
                    date: nextDayAirDate
                }
            )
        })
    }

    return (
        <Box ref={wrapperRef}>
            <Text fontSize={'md'} color={'darkGray'} fontStyle="italic" mb="18px">
                <FormattedMessage
                    id="product_detail.shipping_estimation.disclaimer"
                    defaultMessage="Please note that these delivery dates are estimates; definitive shipping information will be provided at checkout."
                />
            </Text>
            {loading && <Spinner size="sm" />}
            {!loading && (
                <Stack spacing="18px" mb="18px">
                    {estimationRows.map((row) => (
                        <Box key={row.key}>
                            {row.title && (
                                <Text fontSize="md" textTransform="uppercase" mb="6px">
                                    {row.title}
                                </Text>
                            )}
                            <Text fontSize="md" color="darkGray">
                                {row.label && <>{row.label} </>}
                                {row.message}
                            </Text>
                        </Box>
                    ))}
                </Stack>
            )}
            {components.map((component, index) => (
                <Component key={index} component={component} />
            ))}
        </Box>
    )
}
export default PDCShippingPDPDetailsLayout
PDCShippingPDPDetailsLayout.propTypes = {
    regions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    data: PropTypes.object,
                    id: PropTypes.string,
                    typeId: PropTypes.string
                })
            )
        })
    )
}
